/**
 * One section instance — the screen the surveyor spends the whole job on.
 *
 * ONE screen renders all eight of them: Electrical, HVAC, General, Overhead
 * (per hoistway), Pit (per hoistway), Lobbies (per group), Cab Interior and
 * Access. There is no `ElectricalScreen.tsx`, and the absence is the design:
 * every difference between those sections — which questions are asked, in what
 * order, with which options, under which branches — is already stated in the
 * catalog and evaluated by the engines. A per-section component would be a
 * second place for that to be said, and the second place is always the one that
 * goes stale when specification §3 is amended.
 *
 * ── The hand is the hero (D-033, R12) ──────────────────────────────────────
 * The design authority, 2026-09-01: *"make the photo carousel at the top be
 * the hero — the first thing you see. Remove all badges at the top…"* So a
 * section screen is, in order: the photo hand, the questions, the notes, the
 * walk. Nothing renders between the map bar's where-you-are and the hand —
 * not the title eyebrow (the map bar's here-line IS the title), not the
 * context chips (room · type · car count · sprinkler · hoistway · group), not
 * the headline chip (*"…— not answered yet"*), not the tally (*"14 of 18
 * answered"*). Where a surveyor is standing is the map bar's job; which
 * repetition this is (Pit · HW 2) is in that line too. The close panel that
 * used to sit below the notes, just before the walk — the §2.4 photo sentence
 * and the blocked-section flag card — is gone too (R15, R16): the hand's
 * still-to-take cards are the state, and a blocked section is a note.
 *
 * What this screen still supplies:
 *
 *   - The questions, via `QuestionList`, from catalog data alone.
 *   - Walk order. Previous and Next come from `buildSurveySections`, so they
 *     follow the same fixed path the map draws (spec §2.2).
 *
 * Section-level validation issues are NOT repeated here: `QuestionList` already
 * renders each one under the question that owns it, and a chip in two places is
 * two chances to read a different number.
 *
 * ── THE WAY OUT IS THE MAP; THE WAY ON IS THE WALK (D-028) ─────────────────
 * The shell docks the Survey Map bar on every survey screen: one gesture opens
 * the whole job, one tap lands anywhere. This screen keeps ONE control of its
 * own — Previous / Close & next (`WalkButtons`), in flow at the end of the
 * form, labelled with the section they land on — so a surveyor who never opens
 * the map still walks the whole generated survey from the first section to
 * the end of the job.
 *
 * ── THE END OF THE WALK ──────────────────────────────────────────────────────
 * At the far end of the walk the primary button leads to Close & submit ONLY
 * when `guidePlan` says the survey is finishable. Otherwise it leads to the
 * next unwalked thing, so the surveyor is carried on rather than handed a
 * submit screen they cannot use.
 */

import type { ReactNode } from 'react';

import {
  AdHocList,
  NotesField,
  PhotoShelf,
  FlsCutSheetView,
  QuestionList,
  type SectionStop,
  WalkButtons,
} from '../components';
import { asksCatalogQuestions, isStructureSection } from '../../core/sections';
import { STRUCTURAL_QUESTION_IDS } from '../../core/catalog';
import { hrefFor } from '../routing';
import { guidePlan } from '../state/guide';
import { sectionCloseReason } from '../state/sectionClose';
import { useSurvey } from '../state/SurveyProvider';
import { useWalkedSections } from '../state/walked';
import { RoomSetupBody } from './RoomSetupBody';

export interface SectionScreenProps {
  surveyId: string;
  sectionId: string;
}

export function SectionScreen({ surveyId, sectionId }: SectionScreenProps): ReactNode {
  const { results, room } = useSurvey();
  const walked = useWalkedSections(surveyId);
  const section = results.sectionById.get(sectionId);
  const machineRoom = section ? room(section.scope.roomId ?? '') : undefined;

  if (!section) {
    return (
      <>
        <p className="empty">That section doesn&rsquo;t exist on this survey.</p>
        <a className="btn btn--ghost" href={hrefFor({ name: 'building', surveyId })}>
          Back to building
        </a>
      </>
    );
  }

  /*
   * ── THE CHECKLIST SECTIONS — D-023 ───────────────────────────────────────
   * Ad Hoc Items is survey-level (spec §2.5) and its four items (round 7 task
   * 7 retired the free-form "other" that once made a fifth) are not catalog
   * questions at all — they live on `SurveyDocument.adHoc`. The kind is not
   * tested by hand here: `asksCatalogQuestions` is declared beside the walk
   * generator, so a future checklist section gets the checklist treatment by
   * existing rather than by somebody remembering this screen. `AdHocList` is
   * the SAME component the close screen renders, writing the same `doc.adHoc`
   * through the same `setStructure` — one checklist seen twice, never two.
   */
  const catalogSection = asksCatalogQuestions(section.kind);

  /*
   * ── THE STRUCTURE SECTION — Chip, 2026-08-18 ────────────────────────────
   * A structure section's body IS the builder. Same rule as the checklist
   * above: the kind is declared beside the walk generator (`STRUCTURE_SECTIONS`)
   * and consulted here, never tested by hand.
   */
  const structureSection = isStructureSection(section.kind);

  // Walk order comes from the engine, so Previous/Next follow the same fixed
  // path the map draws (spec §2.2). One list, one order — a surveyor who only
  // ever taps Next sees exactly the sequence the lamps promise.
  const index = results.sections.findIndex((candidate) => candidate.id === section.id);
  const previousSection = index > 0 ? results.sections[index - 1] : undefined;
  const nextSection = index >= 0 ? results.sections[index + 1] : undefined;

  const previous: SectionStop | undefined = previousSection
    ? {
        title: previousSection.title,
        href: hrefFor({ name: 'section', surveyId, sectionId: previousSection.id }),
      }
    : undefined;

  /*
   * The wizard, consulted for one thing: what the end of the walk leads to.
   * Mid-walk `next` is still the ENGINE's walk order and nothing else.
   */
  const plan = guidePlan(results, walked);
  const endOfWalk: SectionStop = plan.ready
    ? { title: 'Finish and send this survey', href: hrefFor({ name: 'submit', surveyId }) }
    : { title: plan.step.action, href: hrefFor(plan.step.route) };
  const next: SectionStop = nextSection
    ? {
        title: nextSection.title,
        href: hrefFor({ name: 'section', surveyId, sectionId: nextSection.id }),
      }
    : endOfWalk;
  const atSubmit = nextSection === undefined && plan.ready;

  /*
   * D-030: completeness talks exactly once, at Close & Next, and only when
   * something is actually open — and "open" means PLACEMENT: cars ungrouped
   * or unhoistwayed, structure still missing. An unanswered M4 is an ordinary
   * blank and walks on like any other section's (last look catches it).
   */
  const openPlacement = ((): string | undefined => {
    if (!isStructureSection(section.kind)) return undefined;
    const reason = sectionCloseReason(results, section.id);
    if (reason.finished) return undefined;
    const placement = reason.blockers.filter(
      (blocker) =>
        blocker.kind === 'structure' ||
        (blocker.kind === 'rule' &&
          blocker.questionIds.length > 0 &&
          blocker.questionIds.every((id) => STRUCTURAL_QUESTION_IDS.includes(id))),
    );
    const first = placement[0];
    if (!first) return undefined;
    const more = placement.length - 1;
    return more > 0 ? `${first.headline} · ${String(more)} more` : first.headline;
  })();

  return (
    <>
      {/* The hand leads (D-028, D-032, R12): one card per recommended shot,
       * then the extras, then — round 7 task 5 — a single "Photo 1"
       * placeholder when a section has neither, the add pill pinned in its
       * header. Nothing above it. Room Setup is the one section this screen
       * renders that stays without a shelf at all: the design authority
       * named Building and Room Setup as the two "just configuration
       * screens" that never show the carousel. Building has its own screen
       * and never reaches here; Room Setup DOES reach here (this same
       * screen, `RoomSetupScreen.tsx` is only a door to it), so it is the
       * one kind excluded by hand. */}
      {structureSection ? null : <PhotoShelf section={section} />}

      {structureSection && machineRoom ? (
        <RoomSetupBody section={section} machineRoom={machineRoom} />
      ) : catalogSection ? (
        <>
          <QuestionList section={section} />
          {/*
           * Fire Life Safety — D-027. The panel questions above are the only
           * FLS facts asked HERE; everything else the fire alarm company needs
           * was answered where the surveyor was standing. This is that scope
           * gathered back onto one read-only sheet — the same block the office
           * seals and the report agent prints.
           */}
          {section.kind === 'fls' ? <FlsCutSheetView /> : null}
        </>
      ) : (
        <AdHocList />
      )}

      {/* Notes live at the bottom (D-030 R9): one position, every screen,
       * directly above the walk. A blocked section is written here (R15). */}
      <div className="card notecard">
        <NotesField sectionInstanceId={section.id} />
      </div>

      {/* The walk, in flow at the end of the form (D-028): closing is a link,
       * not a submit — with one amendment (D-030): on a STRUCTURE section
       * with cars still unplaced, the first tap surfaces one line and the
       * choice to fix or continue. Continue is still an ungated link; open
       * placements still travel as gaps. */}
      <WalkButtons
        previous={previous}
        next={next}
        submit={atSubmit}
        {...(structureSection && openPlacement ? { nextGuard: openPlacement } : {})}
        {...(nextSection === undefined && !plan.ready ? { nextFace: 'Keep going' } : {})}
      />
    </>
  );
}
